import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Box, Typography, Alert, CircularProgress,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import axios from 'axios';
import config from '../config';

export interface ComposeEmailData {
  certId: string;
  to: string;
  subject: string;
  body: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  data: ComposeEmailData | null;
  token: string | null;
  onSent?: () => void;
}

const ComposeEmailDialog: React.FC<Props> = ({ open, onClose, data, token, onSent }) => {
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  // Sync fields when data changes (dialog opens with new pre-fill)
  React.useEffect(() => {
    if (data && open) {
      setTo(data.to);
      setSubject(data.subject);
      setBody(data.body);
      setError('');
      setSent(false);
    }
  }, [data, open]);

  const handleSend = async () => {
    if (!data) return;
    setSending(true);
    setError('');
    try {
      const res = await axios.post(
        `${config.apiUrl}/api/certificates/${data.certId}/email-send`,
        { to, subject, body },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data?.success) {
        setSent(true);
        setTimeout(() => { onSent?.(); onClose(); }, 1500);
      } else {
        setError(res.data?.error || 'Failed to send email.');
      }
    } catch (e: any) {
      const msg = e.response?.data?.error || 'Failed to send email.';
      if (msg.toLowerCase().includes('not connected') || msg.toLowerCase().includes('needs_auth')) {
        setError('Gmail is not connected. Go to Integrations to connect your Gmail account first.');
      } else {
        setError(msg);
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography fontWeight={700} fontSize={18}>Contact Provider</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        {sent ? (
          <Alert severity="success">Email sent successfully!</Alert>
        ) : (
          <>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="To"
              value={to}
              onChange={e => setTo(e.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              label="Subject"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              label="Message"
              value={body}
              onChange={e => setBody(e.target.value)}
              multiline
              minRows={8}
              fullWidth
              inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
            />
          </>
        )}
      </DialogContent>

      {!sent && (
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={onClose} variant="text" color="inherit">Cancel</Button>
          <Button
            onClick={handleSend}
            variant="contained"
            disabled={sending || !to || !subject || !body}
            startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            sx={{ borderRadius: 2, fontWeight: 700 }}
          >
            {sending ? 'Sending…' : 'Send Email'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ComposeEmailDialog;
